import { noop } from 'lodash-es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSet } from 'react-use';
import { useOnSaleEvents } from 'src/hooks/api/useOnSaleEvents';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { OnSaleEventWithMetrics } from 'src/navigations/Routes/OnSaleDiscovery/OnSaleDiscoveryView.types';
import { logToServer } from 'src/utils/clientLogUtils';
import {
  DemandDashboardEventIdQueryParam,
  DemandDashboardIsOnSaleEventQueryParam,
} from 'src/utils/constants/constants';
import {
  EventAttributes,
  Feature,
  LogLevel,
  OnSaleEventMetrics,
  OnSaleEventQuery,
  Performer,
  Venue,
} from 'src/WebApiController';

import { useClientLogContext } from '../ClientLogContext';
import { useFilterQueryContext } from '../FilterQueryContext';

export interface IEventDiscoveryContext {
  onSaleEvents: OnSaleEventWithMetrics[];
  performers: Performer[];
  venues: Venue[];
  isLoading: boolean;
  searchText: string | undefined;
  performsVenuesLoaded: boolean;
  setSearchText: (value: string | null) => void;
  onEventsViewed: (eventIds: number[]) => void;
  onEventClick: (eventId: number) => void;
}

const emptyContext: IEventDiscoveryContext = {
  onSaleEvents: [],
  performers: [],
  venues: [],
  isLoading: false,
  searchText: undefined,
  performsVenuesLoaded: false,
  setSearchText: noop,
  onEventsViewed: noop,
  onEventClick: noop,
};

const EventDiscoveryContext =
  createContext<IEventDiscoveryContext>(emptyContext);

export const useEventDiscoveryContext = () => useContext(EventDiscoveryContext);

export const EventDiscoveryContextProvider = ({
  children,
}: PropsWithChildren) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [_, eventsViewedMutation] = useSet<number>();
  // Keep the performers and venues a local state so that it will persist regardless of the query states
  const [performers, setPerformers] = useState<Performer[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const performersAndVenuesLoaded = useRef<boolean>(false);

  const hasOnSaleEventDetailsViewFeature = useUserHasFeature(
    Feature.OnSaleEventDetailsView
  );

  const hasEventDiscoveryInInventoryViewFeature = useUserHasFeature(
    Feature.EventDiscoveryInInventoryView
  );

  const { logEvent } = useClientLogContext();

  const { filterQuery, setFilterQuery } =
    useFilterQueryContext<OnSaleEventQuery>();
  const { onSaleEventMetrics, onSaleEvents, eventAttributes, isLoading } =
    useOnSaleEvents(filterQuery);

  useEffect(() => {
    // Performaners and venues are loaded at the same time
    // and they are the inventory data for all on sale events
    // which should be consistent.
    if (
      !performersAndVenuesLoaded.current &&
      onSaleEvents?.performers &&
      onSaleEvents?.venues
    ) {
      performersAndVenuesLoaded.current = true;
      setPerformers(Object.values(onSaleEvents.performers));
      setVenues(Object.values(onSaleEvents.venues));
    }
  }, [onSaleEvents?.performers, onSaleEvents?.venues]);

  const onEventsViewed = useCallback(
    (eventIds: number[]) => {
      const eventsToLog = [];
      for (const id of new Set(eventIds)) {
        if (eventsViewedMutation.has(id)) {
          continue;
        }
        eventsToLog.push(id);
        eventsViewedMutation.add(id);
      }
      logToServer(
        LogLevel.Information,
        'EventDiscoveryContextProvider',
        'EventViewed',
        { eventIds: eventsToLog },
        logEvent
      );
    },
    [eventsViewedMutation, logEvent]
  );

  const getEventUrl = useCallback(
    (id: number) => {
      if (hasEventDiscoveryInInventoryViewFeature) {
        return `/inventory/event?${DemandDashboardEventIdQueryParam}=${id}&${DemandDashboardIsOnSaleEventQueryParam}=true`;
      }
      if (hasOnSaleEventDetailsViewFeature) {
        return `/discovery/event?${DemandDashboardEventIdQueryParam}=${id}`;
      }
      return `/inventory/event/v2?${DemandDashboardEventIdQueryParam}=${id}&${DemandDashboardIsOnSaleEventQueryParam}=true`;
    },
    [hasEventDiscoveryInInventoryViewFeature, hasOnSaleEventDetailsViewFeature]
  );

  const onEventClick = useCallback(
    (id: number) => {
      window.location.replace(`#${id}`);
      const eventUrl = getEventUrl(id);

      setTimeout(() => {
        navigate(eventUrl, {
          state: {
            returnUrl: `${location.pathname}${location.search}#${id}`,
          },
        });
      }, 10);

      logToServer(
        LogLevel.Information,
        'EventDiscoveryContextProvider',
        'EventClicked',
        { eventId: id },
        logEvent
      );
    },
    [getEventUrl, location.pathname, location.search, logEvent, navigate]
  );

  const mergedEvents = useMemo(() => {
    if (!onSaleEvents) {
      return [];
    }
    const { events, performers, venues } = onSaleEvents;

    const mergedEvents: OnSaleEventWithMetrics[] = Object.entries(events).map(
      ([_, { event }]) => {
        return {
          event,
          performer: event.perfId ? performers[event.perfId] : undefined,
          venue: event.venId ? venues[event.venId] : undefined,
        };
      }
    );
    if (onSaleEventMetrics) {
      const metricsById = onSaleEventMetrics.reduce(
        (res, metrics) => {
          res[metrics.eId] = metrics;
          return res;
        },
        {} as Record<number, OnSaleEventMetrics>
      );
      for (const onSaleEvent of mergedEvents) {
        if (onSaleEvent.event.viagId) {
          const metrics = metricsById[onSaleEvent.event.viagId];
          if (metrics) {
            Object.assign(onSaleEvent, {
              metrics,
            });
          }
        }
      }
    }

    if (eventAttributes) {
      const eventToAttributes = eventAttributes.reduce(
        (res, attributes) => {
          res[attributes.eventId] = attributes;
          return res;
        },
        {} as Record<number, EventAttributes>
      );
      for (const onSaleEvent of mergedEvents) {
        if (onSaleEvent.event.viagId) {
          const eventAttributes =
            eventToAttributes[onSaleEvent.event.viagId] ?? null;
          Object.assign(onSaleEvent, {
            eventAttributes,
          });
        }
      }
    }

    return mergedEvents;
  }, [eventAttributes, onSaleEventMetrics, onSaleEvents]);

  const setSearchText = useCallback(
    (text: string | null) => {
      setFilterQuery({
        ...filterQuery,
        searchText: text,
      });
    },
    [filterQuery, setFilterQuery]
  );

  return (
    <EventDiscoveryContext.Provider
      value={{
        isLoading,
        onSaleEvents: mergedEvents,
        performers,
        venues,
        searchText: filterQuery.searchText ?? undefined,
        performsVenuesLoaded: performersAndVenuesLoaded.current,
        setSearchText,
        onEventsViewed,
        onEventClick,
      }}
    >
      {children}
    </EventDiscoveryContext.Provider>
  );
};
